import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShopMap, LoadingSpinner } from '../../components/index.js';
import { batchService, shopService } from '../../services/index.js';
import { API_URL } from '../../utils/constants.js';
import { cryptoEnabled, decryptPayload, APP_TOKEN } from '../../services/crypto.js';
import {
  isEmbedded,
  postToParent,
  consumePendingCampaignAdd,
  getAppHost,
} from '../../utils/parentBridge.js';
import './CampaignMap.css';

const log = (...args) => console.log('[postcards/campaign-map]', ...args);

// Sorting is FIXED and not configurable: candidates are always taken nearest
// first. This matters here because the backend keeps the first N of the sorted
// pool, so the addresses appended to a campaign are always the closest ones.
const FIXED_SORT = { sortBy: 'distance', sortOrder: 'asc' };

/**
 * Failures that are guidance, not breakage: the user is told what to change
 * (move the area / buy more postcards) instead of being shown a red error.
 */
const WARNING_ERROR_CODES = [
  'DUPLICATE_SEARCH_AREA',
  'NO_POSTCARD_QUOTA',
  'POSTCARD_QUOTA_EXCEEDED',
];

// Stable per-tab client id (so the shop's `shop-updated` SSE — which this page
// doesn't subscribe to — wouldn't echo back; passed for contract parity).
const makeClientId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `c_${Math.floor(performance.now())}_${performance.now()}`;

export const CampaignMap = () => {
  const { nacCampaignId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  // Existing customers of the campaign's shop (loaded once the shop is known),
  // plus their visibility toggle (parent-controlled via postMessage).
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [showCustomers, setShowCustomers] = useState(() => {
    const v = new URLSearchParams(window.location.search).get('showCustomers');
    return !(v === 'false' || v === '0');
  });
  const [focusAddress, setFocusAddress] = useState(null);
  const embedded = isEmbedded();

  // --- Add-addresses-to-this-campaign flow ---------------------------------
  // The SI team, while editing a campaign, can drag the search area to a new
  // point and run a search whose NEW addresses are appended to THIS campaign.
  // Movable search-area center (null = sits on the shop). The shop never moves.
  const [searchArea, setSearchArea] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  // Machine-readable code of the last add failure. DUPLICATE_SEARCH_AREA ("you
  // already searched this exact spot") is rendered as a warning, not an error.
  const [addErrorCode, setAddErrorCode] = useState(null);
  const [addNotice, setAddNotice] = useState('');
  const [addForm, setAddForm] = useState({
    count: 25,
    addressType: 'all', // 'all' | 'houses' | 'apartments'
    searchRadius: 1,
  });
  // Postcard balance of the campaign's shop (Service Intel's
  // NcaPostcardsPurchasedQty) — the ceiling for "how many to add".
  const [quota, setQuota] = useState(null);
  // appHost (needed to push the appended batch to NCA). Resolved from the
  // dedicated `appHost` key OR from the persisted `usr` payload (SI reliably
  // shares `usr`). The per-request appHost from the postMessage still wins.
  const appHost = getAppHost();

  // Latest data kept in a ref so the radius-update listener (registered once)
  // always sees the current campaign shop without re-subscribing.
  const dataRef = useRef(null);
  const clientIdRef = useRef(makeClientId());
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const shopId = data?.shop?._id || null;

  // Ceiling for "how many to add". Null when the balance doesn't apply or
  // couldn't be established — we don't advertise a limit we can't back up, and
  // the backend enforces it either way.
  const quotaMax =
    quota?.applicable && quota?.enforced && Number.isFinite(Number(quota.remaining))
      ? Number(quota.remaining)
      : null;
  const outOfPostcards = quotaMax === 0;

  const refreshQuota = async (targetShopId = shopId) => {
    if (!targetShopId) return;
    try {
      const res = await shopService.getPostcardQuota(targetShopId);
      setQuota(res.data.quota || null);
    } catch (_) {
      /* non-fatal: keep whatever number we had */
    }
  };

  // Read the balance once the campaign's shop is known, and again after each
  // append (the batch just consumed postcards).
  useEffect(() => {
    if (shopId) refreshQuota(shopId);
  }, [shopId]);

  // The postcard balance IS the "how many to add" value: it starts at the full
  // balance and is only editable DOWNWARD. Seeded when the balance arrives (it
  // loads after the first render); later refreshes only ever lower it, never
  // raise a smaller number the user chose. Re-seeds per campaign.
  const countSeededRef = useRef(false);
  useEffect(() => {
    countSeededRef.current = false;
  }, [nacCampaignId]);
  useEffect(() => {
    if (quotaMax == null) return;
    setAddForm((f) => {
      if (!countSeededRef.current) {
        countSeededRef.current = true;
        return { ...f, count: quotaMax };
      }
      const current = Number(f.count);
      return Number.isFinite(current) && current > quotaMax
        ? { ...f, count: quotaMax }
        : f;
    });
  }, [quotaMax, nacCampaignId]);

  // Seed the search radius from the campaign shop once it's known (then leave it
  // under user control). The map circle reflects whatever radius is in the form.
  const radiusSeededRef = useRef(false);
  useEffect(() => {
    const r = data?.shop?.radiusMiles;
    if (r && !radiusSeededRef.current) {
      radiusSeededRef.current = true;
      setAddForm((f) => ({ ...f, searchRadius: r }));
    }
  }, [data?.shop?.radiusMiles]);

  // On load, give SI an initial search center (the campaign shop's location) so
  // an add-batch fired BEFORE any drag still carries coords. The user's drag
  // (emitSearchArea above) overrides it. Emits once per (shop, campaign).
  useEffect(() => {
    const lat = data?.shop?.latitude;
    const lng = data?.shop?.longitude;
    if (lat != null && lng != null && !searchArea) {
      log('emitting initial search-area (shop) to parent', { lat, lng });
      emitSearchArea(lat, lng, { initial: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.shop?.latitude, data?.shop?.longitude, nacCampaignId]);

  // Reset all per-campaign session state when the campaign id changes. Without
  // this, the component instance is reused across campaigns (React Router keeps
  // it mounted, only the param changes) and a previous campaign's dragged center
  // (lat/lng) or radius would leak in — a classic stale-cache bug. We clear the
  // dragged search area and let the radius re-seed from the NEW campaign's shop.
  useEffect(() => {
    setSearchArea(null);
    radiusSeededRef.current = false;
    setAddForm((f) => ({ ...f, searchRadius: 1 }));
  }, [nacCampaignId]);

  // Silent refresh of the campaign map (no full-page spinner) after appending.
  const refreshCampaign = async () => {
    try {
      const res = await batchService.getCampaignMapAddresses(nacCampaignId);
      setData(res.data);
    } catch (err) {
      log('refresh failed', err.message);
    }
  };

  // User dragged the search-area handle on OUR map (the iframe). The map lives in
  // the iframe but Service Intel calls /add-batch from its OWN side, so it can't
  // know where the user moved the pointer unless we tell it. We therefore emit
  // the new search center UP to SI; SI includes these coords (latitude/longitude)
  // in its add-batch call so the search runs around THIS point — connecting the
  // map to the endpoint. (Locally, runAddToCampaign also uses this `searchArea`
  // if the iframe itself triggers the add.)
  const emitSearchArea = (latitude, longitude, extra = {}) => {
    if (latitude == null || longitude == null) return;
    postToParent({
      type: 'search-area-changed',
      data: {
        nacCampaignId: Number(nacCampaignId),
        latitude,
        longitude,
        ...extra,
      },
    });
  };

  const handleSearchAreaChange = ({ latitude, longitude }) => {
    setSearchArea({ latitude, longitude });
    log('search-area moved → emitting to parent', { latitude, longitude });
    emitSearchArea(latitude, longitude);
  };

  // Single place that "tracks" the search radius for THIS campaign session:
  //   1. redraws the coverage circle (addForm.searchRadius + data.shop),
  //   2. REPORTS it back UP to Service Intel so the parent frame stays in sync
  //      and can include the right radiusMiles on its next "Run Search".
  // NOTE: deliberately NO localStorage/global cache — the radius is scoped to
  // this campaign's shop (the DB shop record, PATCHed on radius-update) and to
  // this component's state, which we reset on campaign change. Caching it
  // globally would leak a previous campaign's radius into the next one.
  const applyRadius = (radiusMiles, { report = true } = {}) => {
    const r = Number(radiusMiles);
    if (!Number.isFinite(r) || r <= 0) return;
    setAddForm((f) => ({ ...f, searchRadius: r }));
    setData((prev) =>
      prev ? { ...prev, shop: { ...prev.shop, radiusMiles: r } } : prev
    );
    if (report) {
      postToParent({
        type: 'search-radius-changed',
        data: { nacCampaignId: Number(nacCampaignId), radiusMiles: r },
      });
    }
  };

  // Core "search NEW addresses + append to THIS campaign" routine. Driven from
  // two places that supply overrides:
  //   - the in-iframe modal (non-embedded dev) → uses the form values
  //   - the parent (Service Intel) via the `usr`+`campaignParams` postMessage →
  //     SI supplies Search Mode / Search Radius / Max Records / address type /
  //     campaign name / sort (normalized by <ParentBridge>).
  // Defaults: center on the shop, or the dragged search-area handle if moved;
  // an explicit latitude/longitude in the overrides forces that point.
  const runAddToCampaign = async (overrides = {}) => {
    const shop = data?.shop;
    // Prefer the appHost the parent sent with THIS request; fall back to the one
    // persisted in localStorage from the `usr` message.
    const effectiveAppHost = overrides.appHost || appHost;
    log('runAddToCampaign called', {
      overrides,
      overrideAppHost: overrides.appHost || null,
      localStorageAppHost: appHost || null,
      effectiveAppHost: effectiveAppHost || null,
      shopId: shop?._id || null,
    });

    if (!shop?._id) {
      log('✗ abort: campaign shop not resolved yet');
      setAddError('Campaign shop not resolved yet.');
      return { ok: false, reason: 'no-shop' };
    }
    if (!effectiveAppHost) {
      log('✗ abort: no appHost (not in request override nor localStorage)');
      setAddError('AppHost not available — open this campaign from Service Intel.');
      return { ok: false, reason: 'no-apphost' };
    }

    const count = Number(overrides.maxRecords ?? addForm.count);
    if (!count || count < 1) {
      setAddError('A valid number of addresses to add (Max Records) is required.');
      return { ok: false, reason: 'bad-count' };
    }

    // Resolve the search center: explicit coords > dragged area > shop.
    // IMPORTANT: Number(null) === 0 (and is finite), so a plain isFinite check
    // treats "no coords sent" as a valid (0,0) point — which then forces a
    // location search in the middle of the ocean and returns nothing. Only treat
    // coords as explicit when they're actually present and not (0,0).
    const numOrNull = (v) => (v == null || v === '' ? null : Number(v));
    const oLat = numOrNull(overrides.latitude);
    const oLng = numOrNull(overrides.longitude);
    const hasExplicit =
      oLat != null &&
      oLng != null &&
      Number.isFinite(oLat) &&
      Number.isFinite(oLng) &&
      !(oLat === 0 && oLng === 0);
    const hasArea = searchArea?.latitude != null && searchArea?.longitude != null;
    const center = hasExplicit
      ? { latitude: oLat, longitude: oLng }
      : hasArea
        ? { latitude: searchArea.latitude, longitude: searchArea.longitude }
        : shop.latitude != null && shop.longitude != null
          ? { latitude: shop.latitude, longitude: shop.longitude }
          : null;

    // Address type: prefer the explicit include/only flags SI sends; fall back
    // to the modal's `addressType` selector.
    let includeApartments;
    let onlyApartments;
    if (overrides.includeApartments != null || overrides.onlyApartments != null) {
      onlyApartments = overrides.onlyApartments === true;
      includeApartments = onlyApartments || overrides.includeApartments === true;
    } else {
      const at = overrides.addressType || addForm.addressType;
      onlyApartments = at === 'apartments';
      includeApartments = at !== 'houses';
    }

    const searchRadius =
      Number(overrides.searchRadius) || Number(addForm.searchRadius) || shop.radiusMiles;

    // Search Mode: 'radius' searches around the shop (no center coords needed);
    // 'location' searches around the resolved center. A moved area / explicit
    // coords always imply location. Default location (matches prior behavior).
    const mode =
      overrides.mode === 'radius' && !hasExplicit && !hasArea ? 'radius' : 'location';

    if (mode === 'location' && !center) {
      setAddError('No search center — drag the search area to a point on the map.');
      return { ok: false, reason: 'no-center' };
    }

    try {
      setAdding(true);
      setAddError('');
      setAddErrorCode(null);

      // Live radius: reflect the requested radius on the circle + report it to
      // SI (applyRadius), and — for radius mode, which searches around the shop
      // using shop.radiusMiles — PATCH the shop first so the search honors it
      // (no Melissa cost).
      applyRadius(searchRadius);
      if (mode === 'radius' && searchRadius && Number(shop.radiusMiles) !== Number(searchRadius)) {
        try {
          await shopService.updateShopRadius(shop._id, searchRadius, clientIdRef.current);
        } catch (err) {
          log('radius pre-patch failed (continuing)', err.message);
        }
      }

      const outgoing = {
        shopId: shop._id,
        nacCampaignId,
        count,
        appHost: effectiveAppHost,
        hasAppHost: !!effectiveAppHost,
        mode,
        center,
        searchRadius,
        includeApartments,
        onlyApartments,
        ...FIXED_SORT,
        includeUsed: overrides.includeUsed === true,
        campaignName: overrides.campaignName || undefined,
      };
      log('add-to-campaign → sending', outgoing);
      const res = await batchService.addBatchToCampaign(shop._id, nacCampaignId, {
        count,
        appHost: effectiveAppHost,
        mode,
        latitude: mode === 'location' ? center?.latitude : null,
        longitude: mode === 'location' ? center?.longitude : null,
        searchRadius,
        searchZipCode: overrides.searchZipCode || '',
        searchCity: overrides.searchCity || '',
        searchState: overrides.searchState || '',
        includeApartments,
        onlyApartments,
        campaignName: overrides.campaignName || undefined,
        ...FIXED_SORT,
        includeUsed: overrides.includeUsed === true,
        clientId: clientIdRef.current,
      });
      const added = res?.data?.addressesAdded ?? 0;
      setAddModalOpen(false);
      setAddNotice(
        added > 0
          ? `Added ${added} address(es) to the campaign.`
          : 'No new addresses were added (all nearby were already in the campaign).'
      );
      // We suppress our own SSE echo (clientId), so refresh the map explicitly.
      await refreshCampaign();
      // The appended batch consumed postcards — re-read the balance.
      await refreshQuota();
      // Tell the parent (SI) the outcome of its run-search request.
      postToParent({
        type: 'campaign-addresses-added',
        data: { nacCampaignId: Number(nacCampaignId), addressesAdded: added },
      });
      setTimeout(() => setAddNotice(''), 5000);
      return { ok: true, addressesAdded: added };
    } catch (err) {
      log('add-to-campaign failed', err.message);
      const message = err.message || 'Failed to add addresses to the campaign';
      const duplicateArea = err.errorCode === 'DUPLICATE_SEARCH_AREA';
      // Same spot + same-or-lower count: guidance, not a failure. Shown as a
      // warning; the parent gets the code so it can react on its side too.
      setAddError(message);
      setAddErrorCode(err.errorCode || null);
      // Out of postcards is a business limit — re-read the balance so a refill
      // on Service Intel's side unblocks the next attempt.
      if (
        err.errorCode === 'NO_POSTCARD_QUOTA' ||
        err.errorCode === 'POSTCARD_QUOTA_EXCEEDED'
      ) {
        await refreshQuota();
      }
      postToParent({
        type: 'campaign-addresses-error',
        data: {
          nacCampaignId: Number(nacCampaignId),
          error: message,
          errorCode: err.errorCode || null,
        },
      });
      return {
        ok: false,
        reason: duplicateArea ? 'duplicate-area' : 'error',
        errorCode: err.errorCode || null,
        error: message,
      };
    } finally {
      setAdding(false);
    }
  };

  // The in-iframe modal "Search & Add" button (non-embedded only).
  const handleAddToCampaign = () => runAddToCampaign();

  // Always-fresh ref to runAddToCampaign so the (once-registered) parent
  // listener invokes the latest closure (current `data`, `searchArea`, …).
  const runAddRef = useRef(runAddToCampaign);
  useEffect(() => {
    runAddRef.current = runAddToCampaign;
  });

  // Parent-driven add-to-campaign requests (SI's `usr`+`campaignParams`,
  // normalized + dispatched by <ParentBridge>). We capture the request into
  // `pendingAdd`; a separate effect runs it once the campaign shop is resolved
  // (the very first request can arrive before this page is mounted, so we also
  // drain the buffer <ParentBridge> stashed). Each request runs exactly once
  // (token-guarded).
  const processedAddTokenRef = useRef(null);
  const [pendingAdd, setPendingAdd] = useState(null); // { token, params }

  useEffect(() => {
    const onAdd = (e) => {
      const d = e.detail || {};
      if (Number(d.campaignId) !== Number(nacCampaignId)) return;
      log('parent add-to-campaign request', { token: d.token, params: d.params });
      // Captured live → clear the bridge's stash so a later remount can't replay it.
      consumePendingCampaignAdd(nacCampaignId);
      setPendingAdd({ token: d.token, params: d.params, appHost: d.appHost });
    };
    window.addEventListener('postcards:add-to-campaign', onAdd);
    // Drain a request the bridge stashed before this page mounted.
    const buffered = consumePendingCampaignAdd(nacCampaignId);
    if (buffered)
      setPendingAdd({ token: buffered.token, params: buffered.params, appHost: buffered.appHost });
    return () => window.removeEventListener('postcards:add-to-campaign', onAdd);
  }, [nacCampaignId]);

  useEffect(() => {
    if (!pendingAdd || !shopId || adding) return;
    if (processedAddTokenRef.current === pendingAdd.token) return;
    processedAddTokenRef.current = pendingAdd.token;
    const { params, appHost: reqAppHost } = pendingAdd;
    setPendingAdd(null);
    runAddRef.current({ ...params, appHost: reqAppHost });
  }, [pendingAdd, shopId, adding]);

  useEffect(() => {
    let cancelled = false;

    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError('');
        log('fetching campaign addresses', nacCampaignId);
        const res = await batchService.getCampaignMapAddresses(nacCampaignId);
        if (cancelled) return;
        log('campaign addresses received', {
          totalAddresses: res?.data?.totalAddresses,
          batchCount: res?.data?.batchCount,
        });
        setData(res.data);
      } catch (err) {
        if (cancelled) return;
        console.error('[postcards/campaign-map] ✗ fetch failed', err);
        setError(err.message || 'Failed to load campaign addresses');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCampaign();
    return () => {
      cancelled = true;
    };
  }, [nacCampaignId]);

  // Real-time: refresh the campaign map when a batch is added to THIS campaign
  // (e.g. via the add-batch webhook). Scoped to this nacCampaignId only.
  useEffect(() => {
    if (!nacCampaignId) return undefined;

    const params = new URLSearchParams();
    if (APP_TOKEN) params.set('token', APP_TOKEN);
    const url = `${API_URL}/batches/campaign/${nacCampaignId}/stream?${params.toString()}`;
    const es = new EventSource(url);

    es.addEventListener('campaign-updated', async (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const payload =
          cryptoEnabled() && parsed && typeof parsed.p === 'string'
            ? await decryptPayload(parsed.p)
            : parsed;
        log('SSE campaign-updated', {
          totalAddresses: payload?.totalAddresses,
          batchCount: payload?.batchCount,
        });
        if (payload?.addresses) setData(payload);
      } catch (err) {
        log('SSE campaign-updated failed', err.message);
      }
    });

    es.onerror = () => log('SSE connection error (will auto-retry)');

    return () => es.close();
  }, [nacCampaignId]);

  // Live radius edit from the parent (postmessage-radius.md), re-broadcast by
  // <ParentBridge> as a `postcards:radius-update` CustomEvent. In campaign mode
  // the coverage circle belongs to the campaign's shop, so we resolve the
  // shopId from the loaded campaign data, PATCH the radius, and update the local
  // shop so ShopMap redraws the circle. No address search.
  useEffect(() => {
    const onRadiusUpdate = async (e) => {
      const radiusMiles = Number(e.detail?.radiusMiles);
      if (!Number.isFinite(radiusMiles) || radiusMiles < 0.1 || radiusMiles > 50) return;

      const shopId = dataRef.current?.shop?._id;
      if (!shopId) {
        log('radius-update ignored — no shop resolved for this campaign');
        return;
      }

      try {
        log('parent radius-update', { radiusMiles, shopId });
        const res = await shopService.updateShopRadius(shopId, radiusMiles, clientIdRef.current);
        const updatedRadius = res?.data?.shop?.radiusMiles ?? radiusMiles;
        // Redraw the circle + persist it (so add-batch reads it) + report it
        // back to SI so the parent frame and our state stay in sync.
        applyRadius(updatedRadius);
      } catch (err) {
        log('parent radius-update failed', err.message);
      }
    };

    window.addEventListener('postcards:radius-update', onRadiusUpdate);
    return () => window.removeEventListener('postcards:radius-update', onRadiusUpdate);
  }, []);

  // Once the campaign's shop is known, load its existing customers and keep
  // them live: subscribe to the SHOP's SSE stream for `customers-updated`
  // (Service Intel pushed a batch) and `shop-updated` (radius changed
  // elsewhere). This is what makes existing customers real-time in the campaign
  // view too. Our own radius PATCH is suppressed via cid.
  useEffect(() => {
    if (!shopId) return undefined;
    let cancelled = false;

    shopService
      .getCustomersForMap(shopId)
      .then((res) => {
        if (!cancelled) setCustomerAddresses(res?.data?.customers || []);
      })
      .catch((err) => log('customers fetch failed', err.message));

    const params = new URLSearchParams();
    if (APP_TOKEN) params.set('token', APP_TOKEN);
    params.set('cid', clientIdRef.current);
    const url = `${API_URL}/shops/${shopId}/stream?${params.toString()}`;
    const es = new EventSource(url);

    es.addEventListener('customers-updated', async (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const payload =
          cryptoEnabled() && parsed && typeof parsed.p === 'string'
            ? await decryptPayload(parsed.p)
            : parsed;
        if (payload?.customers) {
          log('SSE customers-updated', { customers: payload.customers.length });
          setCustomerAddresses(payload.customers);
        }
      } catch (err) {
        log('SSE customers-updated failed', err.message);
      }
    });

    es.addEventListener('shop-updated', async (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const payload =
          cryptoEnabled() && parsed && typeof parsed.p === 'string'
            ? await decryptPayload(parsed.p)
            : parsed;
        if (payload?.shop) {
          log('SSE shop-updated', { radiusMiles: payload.shop.radiusMiles });
          if (payload.shop.radiusMiles != null) {
            // Reflect + persist; don't re-report (avoids ping-pong with SI).
            applyRadius(payload.shop.radiusMiles, { report: false });
          }
        }
      } catch (err) {
        log('SSE shop-updated failed', err.message);
      }
    });

    es.onerror = () => log('shop SSE connection error (will auto-retry)');

    return () => {
      cancelled = true;
      es.close();
    };
  }, [shopId]);

  // Live show/hide of existing-customer markers (parent postMessage → CustomEvent).
  useEffect(() => {
    const onToggleCustomers = (e) => {
      const show = e.detail?.show !== false;
      log('parent existing-customers', { show });
      setShowCustomers(show);
    };
    window.addEventListener('postcards:existing-customers', onToggleCustomers);
    return () => window.removeEventListener('postcards:existing-customers', onToggleCustomers);
  }, []);

  // SI focus/highlight a point: match by melissaAddressKey/coords against the
  // campaign addresses + existing customers, else fall back to plain coords.
  const focusPoolsRef = useRef({ addresses: [], customers: [] });
  useEffect(() => {
    focusPoolsRef.current = { addresses: data?.addresses || [], customers: customerAddresses };
  }, [data, customerAddresses]);

  useEffect(() => {
    const onFocus = (e) => {
      const d = e.detail || {};
      const pool = [...focusPoolsRef.current.addresses, ...focusPoolsRef.current.customers];
      let match = null;
      if (d.melissaAddressKey) {
        match = pool.find((a) => a.melissaAddressKey === d.melissaAddressKey) || null;
      }
      if (!match && d.latitude != null && d.longitude != null) {
        match = pool.find(
          (a) =>
            Math.abs(a.latitude - d.latitude) < 1e-6 &&
            Math.abs(a.longitude - d.longitude) < 1e-6
        ) || null;
      }
      if (match) setFocusAddress(match);
      else if (d.latitude != null && d.longitude != null)
        setFocusAddress({ latitude: d.latitude, longitude: d.longitude });
    };
    window.addEventListener('postcards:focus-address', onFocus);
    return () => window.removeEventListener('postcards:focus-address', onFocus);
  }, []);

  if (loading) {
    return (
      <div className="container-campaignmap">
        <LoadingSpinner size="large" message="Loading campaign addresses…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-campaignmap">
        <div className="error-campaignmap">{error}</div>
      </div>
    );
  }

  const addresses = data?.addresses || [];
  const hasAddresses = addresses.length > 0;

  return (
    <div className="container-campaignmap">
      {/* Header hidden inside the SI embed (the parent already shows it). */}
      {!embedded && (
        <div className="header-campaignmap">
          <div className="title-group-campaignmap">
            <h1 className="title-campaignmap">
              {data?.campaignName || `Campaign #${data?.nacCampaignId}`}
            </h1>
            <span className="subtitle-campaignmap">
              NCA #{data?.nacCampaignId}
              {data?.shop?.shopName ? ` • ${data.shop.shopName}` : ''}
            </span>
          </div>
          <div className="stats-campaignmap">
            <span className="stat-campaignmap">
              <strong>{data?.totalAddresses ?? 0}</strong> addresses
            </span>
            <span className="stat-campaignmap">
              <strong>{data?.batchCount ?? 0}</strong> batch
              {data?.batchCount === 1 ? '' : 'es'}
            </span>
          </div>
        </div>
      )}

      {!hasAddresses ? (
        <div className="empty-campaignmap">
          No addresses found for this campaign.
        </div>
      ) : (
        <>
          {/* Action bar — only OUTSIDE the SI embed. Inside the embed, SI drives
              "add addresses" entirely via the `usr`+`campaignParams` postMessage
              (see ADD_TO_CAMPAIGN_API.md §6); the in-iframe button is hidden. */}
          {!embedded && (
          <div className="actionbar-campaignmap">
            <span className="actionbar-hint-campaignmap">
              Drag the search area on the map, then add new addresses to this campaign.
            </span>
            <div className="actionbar-msgs-campaignmap">
              {addNotice && <span className="notice-campaignmap">{addNotice}</span>}
              {addError && (
                <span
                  className={`adderror-campaignmap ${
                    WARNING_ERROR_CODES.includes(addErrorCode) ? 'warn-campaignmap' : ''
                  }`}
                >
                  {addError}
                </span>
              )}
            </div>
            <button
              type="button"
              className="add-btn-campaignmap"
              onClick={() => {
                setAddError('');
                setAddModalOpen(true);
              }}
              disabled={!shopId}
              title={
                shopId
                  ? 'Search and add new addresses to this campaign'
                  : 'Campaign shop not resolved yet'
              }
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Add Addresses
            </button>
          </div>
          )}

          <div className="map-wrap-campaignmap">
            <ShopMap
              shop={data?.shop || null}
              batchAddresses={addresses}
              customerAddresses={customerAddresses}
              showCustomers={showCustomers}
              focusAddress={focusAddress}
              searchArea={searchArea}
              onSearchAreaChange={handleSearchAreaChange}
              searchRadius={addForm.searchRadius}
              fitToAddresses
              showLegend
              height="100%"
            />
          </div>
        </>
      )}

      {/* Add-addresses-to-campaign modal */}
      {addModalOpen && (
        <div
          className="modal-backdrop-campaignmap"
          onClick={() => !adding && setAddModalOpen(false)}
        >
          <div className="modal-campaignmap" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-campaignmap">
              <h2 className="modal-title-campaignmap">Add Addresses to Campaign</h2>
              <button
                type="button"
                className="modal-close-campaignmap"
                onClick={() => setAddModalOpen(false)}
                disabled={adding}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body-campaignmap">
              <p className="modal-desc-campaignmap">
                {searchArea
                  ? 'New addresses will be searched around the moved search area.'
                  : 'Tip: drag the search area on the map first to target a new point. Without dragging, the search is centered on the shop.'}
              </p>

              <div className="form-grid-campaignmap">
                <div className="field-campaignmap">
                  <label>How many to add</label>
                  {/* Editable select: preset options via datalist + custom typing.
                      Ceiling is the shop's postcard balance when known. */}
                  <input
                    type="number"
                    min="1"
                    max={quotaMax ?? 1000}
                    step="1"
                    list="maxrecords-options-campaignmap"
                    value={addForm.count}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') return setAddForm((f) => ({ ...f, count: '' }));
                      const n = parseInt(raw, 10);
                      return setAddForm((f) => ({
                        ...f,
                        count: quotaMax != null ? Math.min(n, quotaMax) : n,
                      }));
                    }}
                  />
                  <datalist id="maxrecords-options-campaignmap">
                    {[5, 10, 25, 50, 100, 200, 500, 1000]
                      .filter((n) => quotaMax == null || n <= quotaMax)
                      .map((n) => (
                        <option key={n} value={n} />
                      ))}
                  </datalist>
                  {quotaMax != null && (
                    <span className={`quota-hint-campaignmap ${outOfPostcards ? 'empty' : ''}`}>
                      {outOfPostcards
                        ? 'No postcards left — purchase more in Service Intel'
                        : `${quotaMax} postcard${quotaMax === 1 ? '' : 's'} available`}
                    </span>
                  )}
                </div>
                <div className="field-campaignmap">
                  <label>Search Radius (mi)</label>
                  <input
                    type="number"
                    min="0.1"
                    max="50"
                    step="0.5"
                    value={addForm.searchRadius}
                    onChange={(e) =>
                      setAddForm((f) => ({ ...f, searchRadius: e.target.value }))
                    }
                  />
                </div>
                <div className="field-campaignmap">
                  <label>Address Type</label>
                  <select
                    value={addForm.addressType}
                    onChange={(e) =>
                      setAddForm((f) => ({ ...f, addressType: e.target.value }))
                    }
                  >
                    <option value="all">SFH + Apartments</option>
                    <option value="houses">Houses only (SFH)</option>
                    <option value="apartments">Apartments only</option>
                  </select>
                </div>
                {/* Sort By / Order removed — addresses are always taken
                    nearest-first (see FIXED_SORT). */}
              </div>

              {addError && (
                <div
                  className={`adderror-campaignmap ${
                    WARNING_ERROR_CODES.includes(addErrorCode) ? 'warn-campaignmap' : ''
                  }`}
                >
                  {addError}
                </div>
              )}

              <button
                type="button"
                className="submit-btn-campaignmap"
                onClick={handleAddToCampaign}
                disabled={adding || outOfPostcards}
              >
                {adding
                  ? 'Searching & adding…'
                  : outOfPostcards
                    ? 'No postcards available'
                    : 'Search & Add to Campaign (Uses Credits)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignMap;
